import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Menu, X, Globe } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const LOGO_AVIF = 'https://customer-assets.emergentagent.com/job_3ec12bb3-915e-4bbb-87b2-e085aa4a2c01/artifacts/jfotexpw_Logo%20Wee%20V2%20-%202026%20-%20SBG.HQ.avif';
const LOGO_WEBP = 'https://customer-assets.emergentagent.com/job_3ec12bb3-915e-4bbb-87b2-e085aa4a2c01/artifacts/76ox2huh_Logo%20Wee%20V2%20-%202026%20-%20SBG.HQ.webp';

const SECTION_IDS = ['slogan', 'quem-somos', 'proposito', 'produtos', 'contato'];

export default function Header({ activeSection }) {
  const { language, switchLanguage, t } = useLanguage();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const scrollTo = (id) => {
    if (location.pathname === '/') {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/', { state: { scrollTo: id } });
    }
    setMobileOpen(false);
  };

  const navLinks = [
    { id: 'quem-somos', label: t.nav.quemSomos, color: '#6E2D60' },
    { id: 'produtos', label: t.nav.produtos, color: '#2C4A85' },
    { id: 'contato', label: t.nav.contato, color: '#6E2D60' },
  ];

  return (
    <header
      data-testid="main-header"
      className="fixed top-0 left-0 w-full z-50 backdrop-blur-md bg-[#F9F8F6]/80 border-b border-[#333333]/5 transition-all duration-300"
    >
      <div className="flex items-center justify-between py-3 px-6 md:px-12">
        {/* Left: Logo + Company Name */}
        <div className="flex items-center gap-3 cursor-pointer" data-testid="header-logo" onClick={() => navigate('/')}>
          <picture>
            <source srcSet={LOGO_AVIF} type="image/avif" />
            <source srcSet={LOGO_WEBP} type="image/webp" />
            <img
              src={LOGO_WEBP}
              alt="Wee Time"
              fetchPriority="high"
              decoding="async"
              width={32}
              height={32}
              className="h-8 w-8 object-contain"
            />
          </picture>
          <span className="text-[#333333] font-light text-lg tracking-wide">
            Wee Time
          </span>
        </div>

        {/* Center/Right: Nav Links (desktop) */}
        <nav className="hidden md:flex items-center gap-10" data-testid="desktop-nav">
          {navLinks.map((link) => (
            <button
              key={link.id}
              data-testid={`nav-link-${link.id}`}
              onClick={() => scrollTo(link.id)}
              className={`text-sm font-light tracking-wider transition-colors duration-300 hover:underline`}
              style={{ color: link.color }}
            >
              {link.label}
            </button>
          ))}

          {/* Language Selector */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="header-language-selector"
                className="flex items-center gap-1.5 text-sm font-light tracking-wider text-[#333333]/90 hover:text-[#35589B] transition-colors duration-300"
              >
                <Globe size={14} />
                <span>{language.toUpperCase()}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-[#F9F8F6] border-[#333333]/10 min-w-[120px]"
            >
              <DropdownMenuItem
                data-testid="header-lang-pt"
                onClick={() => switchLanguage('pt')}
                className={`text-sm font-light cursor-pointer ${
                  language === 'pt' ? 'text-[#35589B]' : 'text-[#333333]/90'
                }`}
              >
                Português
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="header-lang-en"
                onClick={() => switchLanguage('en')}
                className={`text-sm font-light cursor-pointer ${
                  language === 'en' ? 'text-[#35589B]' : 'text-[#333333]/90'
                }`}
              >
                English
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>

        {/* Mobile: Language + Hamburger */}
        <div className="flex md:hidden items-center gap-4">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="mobile-language-selector"
                className="flex items-center gap-1 text-sm font-light text-[#333333]/90"
              >
                <Globe size={14} />
                <span>{language.toUpperCase()}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-[#F9F8F6] border-[#333333]/10"
            >
              <DropdownMenuItem
                onClick={() => switchLanguage('pt')}
                className={`text-sm font-light cursor-pointer ${
                  language === 'pt' ? 'text-[#35589B]' : 'text-[#333333]/90'
                }`}
              >
                Português
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => switchLanguage('en')}
                className={`text-sm font-light cursor-pointer ${
                  language === 'en' ? 'text-[#35589B]' : 'text-[#333333]/90'
                }`}
              >
                English
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <button
            data-testid="mobile-menu-toggle"
            aria-label={language === 'pt' ? 'Menu' : 'Menu'}
            onClick={() => setMobileOpen(!mobileOpen)}
            className="text-[#333333]/90 hover:text-[#35589B] transition-colors"
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation Overlay */}
      {mobileOpen && (
        <div
          data-testid="mobile-nav-overlay"
          className="md:hidden bg-[#F9F8F6]/95 backdrop-blur-lg border-t border-[#333333]/5"
        >
          <nav className="flex flex-col py-6 px-8 gap-6">
            {navLinks.map((link) => (
              <button
                key={link.id}
                data-testid={`mobile-nav-${link.id}`}
                onClick={() => scrollTo(link.id)}
                className={`text-left text-base font-light tracking-wider transition-colors duration-300`}
                style={{ color: link.color }}
              >
                {link.label}
              </button>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}
